import { toDisplayText } from "../../../../utils/localizedText";

const createBaseNode = ({
  id,
  label,
  values,
  type,
  level,
  isOpen = false,
}) => ({
  id,
  label: toDisplayText(label, id),
  values,
  type,
  level,
  isOpen,
  children: [],
});

const buildHeaders = (apiData, texts) => [
  "ACCOUNT",
  toDisplayText(apiData.columns?.[0]?.label, "OCT 2025"),
  texts?.reports?.percentages || "PERCENTAGES",
  texts?.reports?.details || "DETAILS WITH AI",
];

const calculatePercentage = (value, total) => {
  const absoluteValue = Math.abs(parseFloat(value) || 0);
  const absoluteTotal = Math.abs(parseFloat(total) || 0);

  if (!absoluteTotal) {
    return "0.00%";
  }

  return `${((absoluteValue / absoluteTotal) * 100).toFixed(2)}%`;
};

const transformSingleColumnTree = (apiData, texts, getAccountPercentage) => {
  if (!apiData?.columns || !apiData?.rows) {
    return { headers: [], treeData: [] };
  }

  const headers = buildHeaders(apiData, texts);
  const treeData = apiData.rows.map((mainGroup) => {
    const mainGroupNode = createBaseNode({
      id: `group-${mainGroup.id}`,
      label: mainGroup.label,
      values: ["", "", ""],
      type: "group",
      level: 0,
      isOpen: true,
    });

    const mainGroupTotal = mainGroup.summary?.sub_totals?.[0] || 0;

    (mainGroup.children || []).forEach((subGroup) => {
      const subGroupNode = createBaseNode({
        id: `subgroup-${subGroup.id}`,
        label: subGroup.label,
        values: ["", "", ""],
        type: "subgroup",
        level: 1,
        isOpen: true,
      });

      const appendAccount = (account, level) => {
        const accountValue = parseFloat(account.values?.[0]) || 0;
        subGroupNode.children.push(
          createBaseNode({
            id: `account-${account.id}`,
            label: account.label,
            values: [
              accountValue,
              getAccountPercentage({
                accountValue,
                mainGroupTotal,
                subGroupTotal: subGroup.summary?.sub_totals?.[0] || 0,
              }),
              "",
            ],
            type: "account",
            level,
          }),
        );

        (account.children || []).forEach((nestedAccount) => {
          appendAccount(nestedAccount, level + 1);
        });
      };

      (subGroup.children || []).forEach((account) => appendAccount(account, 2));

      if (subGroup.summary) {
        const subGroupTotal = subGroup.summary.sub_totals?.[0] || 0;
        subGroupNode.children.push(
          createBaseNode({
            id: `subtotal-${subGroup.summary.id}`,
            label: subGroup.summary.label,
            values: [
              subGroupTotal,
              calculatePercentage(subGroupTotal, mainGroupTotal),
              "",
            ],
            type: "subtotal",
            level: 2,
          }),
        );
      }

      mainGroupNode.children.push(subGroupNode);
    });

    if (mainGroup.summary) {
      const totalAllGroups = apiData.rows.reduce((total, group) => {
        return total + Math.abs(group.summary?.sub_totals?.[0] || 0);
      }, 0);

      mainGroupNode.children.push(
        createBaseNode({
          id: `grouptotal-${mainGroup.summary.id}`,
          label: mainGroup.summary.label,
          values: [
            mainGroupTotal,
            calculatePercentage(mainGroupTotal, totalAllGroups),
            "",
          ],
          type: "grouptotal",
          level: 1,
        }),
      );
    }

    return mainGroupNode;
  });

  return { headers, treeData };
};

export const transformToTreeData = (apiData, texts) =>
  transformSingleColumnTree(apiData, texts, ({ accountValue, subGroupTotal }) =>
    calculatePercentage(accountValue, subGroupTotal),
  );

export const transformToTreeDataAnalytical = (apiData, texts) =>
  transformSingleColumnTree(
    apiData,
    texts,
    ({ accountValue, mainGroupTotal }) =>
      calculatePercentage(accountValue, mainGroupTotal),
  );

export const transformToTreeDataStructuralRatios = (apiData, texts) =>
  transformSingleColumnTree(
    apiData,
    texts,
    ({ subGroupTotal, mainGroupTotal }) =>
      calculatePercentage(subGroupTotal, mainGroupTotal),
  );

export const transformToTreeTimeSeries = (apiData, texts) => {
  if (!apiData?.columns || !apiData?.rows) {
    return { headers: [], treeData: [] };
  }

  const headers = [
    "ACCOUNT",
    ...apiData.columns.flatMap((col) => {
      const label = toDisplayText(col.label, col.id || "Period");
      return [label, `${label} %`];
    }),
    texts?.reports?.details || "DETAILS WITH AI",
  ];

  const treeData = apiData.rows.map((mainGroup) => {
    const valueSlots = Array(apiData.columns.length * 2 + 1).fill("");
    const mainGroupNode = createBaseNode({
      id: `group-${mainGroup.id}`,
      label: mainGroup.label,
      values: valueSlots,
      type: "group",
      level: 0,
      isOpen: true,
    });

    (mainGroup.children || []).forEach((subGroup) => {
      const subGroupNode = createBaseNode({
        id: `subgroup-${subGroup.id}`,
        label: subGroup.label,
        values: [...valueSlots],
        type: "subgroup",
        level: 1,
        isOpen: true,
      });

      const appendAccount = (account, level) => {
        const accountValues =
          account.values || Array(apiData.columns.length).fill(0);
        const combinedValues = [];

        apiData.columns.forEach((_, index) => {
          const mainGroupTotal = mainGroup.summary?.sub_totals?.[index] || 0;
          combinedValues.push(
            accountValues[index],
            calculatePercentage(accountValues[index], mainGroupTotal),
          );
        });

        combinedValues.push("");

        subGroupNode.children.push(
          createBaseNode({
            id: `account-${account.id}`,
            label: account.label,
            values: combinedValues,
            type: "account",
            level,
          }),
        );

        (account.children || []).forEach((nestedAccount) => {
          appendAccount(nestedAccount, level + 1);
        });
      };

      (subGroup.children || []).forEach((account) => appendAccount(account, 2));

      if (subGroup.summary) {
        const combinedValues = [];
        const subTotals = subGroup.summary.sub_totals || [];

        apiData.columns.forEach((_, index) => {
          const mainGroupTotal = mainGroup.summary?.sub_totals?.[index] || 0;
          const value = subTotals[index] || 0;
          combinedValues.push(
            value,
            calculatePercentage(value, mainGroupTotal),
          );
        });

        combinedValues.push("");

        subGroupNode.children.push(
          createBaseNode({
            id: `subtotal-${subGroup.summary.id}`,
            label: subGroup.summary.label,
            values: combinedValues,
            type: "subtotal",
            level: 2,
          }),
        );
      }

      mainGroupNode.children.push(subGroupNode);
    });

    if (mainGroup.summary) {
      const combinedValues = [];
      const mainTotals = mainGroup.summary.sub_totals || [];

      apiData.columns.forEach((_, index) => {
        const totalAllGroups = apiData.rows.reduce((total, group) => {
          return total + Math.abs(group.summary?.sub_totals?.[index] || 0);
        }, 0);
        const value = mainTotals[index] || 0;
        combinedValues.push(value, calculatePercentage(value, totalAllGroups));
      });

      combinedValues.push("");

      mainGroupNode.children.push(
        createBaseNode({
          id: `grouptotal-${mainGroup.summary.id}`,
          label: mainGroup.summary.label,
          values: combinedValues,
          type: "grouptotal",
          level: 1,
        }),
      );
    }

    return mainGroupNode;
  });

  return { headers, treeData };
};
